import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search,
  Plus,
  Copy,
  Check,
  Pencil,
  X,
  Trash2,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  FolderOpen,
  Folder,
  FileText,
  LayoutList,
  SortAsc,
  AlertCircle,
  Loader2,
  Menu,
} from 'lucide-react';
import axios from 'axios';
import './PromptCollection.css';

// --- Types ---

interface PromptCategory {
  _id: string;
  name: string;
  parent: string | null;
  order: number;
  count: number;
}

interface Prompt {
  _id: string;
  title: string;
  description: string;
  text: string;
  category: { _id: string; name: string } | string;
  createdAt: string;
  updatedAt: string;
}

type SortMode = 'newest' | 'oldest' | 'title';

// --- API ---

const API_BASE = '/api/prompts';

const api = {
  getCategories: () => axios.get<PromptCategory[]>(`${API_BASE}/categories`),
  createCategory: (name: string, parent?: string) => axios.post<PromptCategory>(`${API_BASE}/categories`, { name, parent: parent || undefined }),
  updateCategory: (id: string, name: string) => axios.put<PromptCategory>(`${API_BASE}/categories/${id}`, { name }),
  deleteCategory: (id: string) => axios.delete(`${API_BASE}/categories/${id}`),
  getPrompts: (params?: { category?: string; search?: string; sort?: string }) =>
    axios.get<Prompt[]>(API_BASE, { params }),
  createPrompt: (data: { title: string; description: string; text: string; category: string }) =>
    axios.post<Prompt>(API_BASE, data),
  updatePrompt: (id: string, data: Partial<{ title: string; description: string; text: string; category: string }>) =>
    axios.put<Prompt>(`${API_BASE}/${id}`, data),
  deletePrompt: (id: string) => axios.delete(`${API_BASE}/${id}`),
  seed: () => axios.post(`${API_BASE}/seed`),
};

// --- Helper ---

function getCategoryId(prompt: Prompt): string {
  if (typeof prompt.category === 'string') return prompt.category;
  return prompt.category?._id || '';
}

function getCategoryName(prompt: Prompt): string {
  if (typeof prompt.category === 'object' && prompt.category) return prompt.category.name;
  return '';
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'gerade eben';
  if (mins < 60) return `vor ${mins} Min.`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `vor ${hrs} Std.`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `vor ${days} Tag${days > 1 ? 'en' : ''}`;
  return new Date(dateStr).toLocaleDateString('de-DE');
}

// --- Toast Component ---

interface ToastData {
  id: number;
  message: string;
  type: 'success' | 'error';
}

const Toast: React.FC<{ toasts: ToastData[]; onRemove: (id: number) => void }> = ({ toasts, onRemove }) => (
  <div className="pc-toast-container">
    <AnimatePresence>
      {toasts.map(toast => (
        <motion.div
          key={toast.id}
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className={`pc-toast pc-toast--${toast.type}`}
          onClick={() => onRemove(toast.id)}
        >
          {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
          <span>{toast.message}</span>
        </motion.div>
      ))}
    </AnimatePresence>
  </div>
);

// --- Main Component ---

const PromptCollection: React.FC = () => {
  // Data
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortMode, setSortMode] = useState<SortMode>('newest');
  const [showSortDropdown, setShowSortDropdown] = useState(false);

  // Editor
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState<Prompt | null>(null);
  const [editorTitle, setEditorTitle] = useState('');
  const [editorDescription, setEditorDescription] = useState('');
  const [editorText, setEditorText] = useState('');
  const [editorCategory, setEditorCategory] = useState('');
  const [editorSaving, setEditorSaving] = useState(false);

  // Category editor
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [addingSubcategoryFor, setAddingSubcategoryFor] = useState<string | null>(null);
  const [newSubcategoryName, setNewSubcategoryName] = useState('');

  // Expanded parent categories in sidebar
  const [expandedParents, setExpandedParents] = useState<Set<string>>(new Set());

  // Expanded prompt cards
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  // Copied state per prompt
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Delete confirm
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Toast
  const [toasts, setToasts] = useState<ToastData[]>([]);
  const toastCounter = useRef(0);

  // Mobile sidebar
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Debounce
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const sortDropdownRef = useRef<HTMLDivElement>(null);

  // --- Toast helper ---
  const addToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    const id = ++toastCounter.current;
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 2500);
  }, []);

  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  // --- Debounced search ---
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setDebouncedSearch(searchQuery), 300);
    return () => { if (searchTimeout.current) clearTimeout(searchTimeout.current); };
  }, [searchQuery]);

  // --- Close sort dropdown on outside click ---
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(e.target as Node)) {
        setShowSortDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // --- Fetch categories ---
  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.getCategories();
      setCategories(res.data);
    } catch {
      // Seed and retry
      try {
        await api.seed();
        const res = await api.getCategories();
        setCategories(res.data);
      } catch (err: any) {
        setError('Kategorien konnten nicht geladen werden.');
      }
    }
  }, []);

  // --- Fetch prompts ---
  const fetchPrompts = useCallback(async () => {
    try {
      const params: any = {};
      if (activeCategory) params.category = activeCategory;
      if (debouncedSearch) params.search = debouncedSearch;
      if (sortMode === 'title') params.sort = 'title';
      if (sortMode === 'oldest') params.sort = 'oldest';
      const res = await api.getPrompts(params);
      setPrompts(res.data);
    } catch {
      setError('Prompts konnten nicht geladen werden.');
    }
  }, [activeCategory, debouncedSearch, sortMode]);

  // --- Initial load ---
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await fetchCategories();
      setLoading(false);
    };
    init();
  }, [fetchCategories]);

  // --- Reload prompts on filter change ---
  useEffect(() => {
    fetchPrompts();
  }, [fetchPrompts]);

  // --- Total count ---
  const totalCount = categories.reduce((sum, c) => sum + c.count, 0);

  // --- Category tree helpers ---
  const parentCategories = categories.filter(c => !c.parent);
  const getChildren = (parentId: string) => categories.filter(c => c.parent === parentId);
  const getParentCount = (parentId: string) => {
    const parent = categories.find(c => c._id === parentId);
    const children = getChildren(parentId);
    return (parent?.count || 0) + children.reduce((sum, c) => sum + c.count, 0);
  };
  const toggleParentExpand = useCallback((id: string) => {
    setExpandedParents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Copy ---
  const handleCopy = useCallback(async (prompt: Prompt) => {
    try {
      await navigator.clipboard.writeText(prompt.text);
      setCopiedId(prompt._id);
      addToast('Kopiert');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      addToast('Kopieren fehlgeschlagen', 'error');
    }
  }, [addToast]);

  // --- Expand/Collapse ---
  const toggleExpand = useCallback((id: string) => {
    setExpandedCards(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // --- Open editor ---
  const openCreateEditor = useCallback(() => {
    setEditingPrompt(null);
    setEditorTitle('');
    setEditorDescription('');
    setEditorText('');
    setEditorCategory(activeCategory || (categories[0]?._id || ''));
    setEditorOpen(true);
  }, [activeCategory, categories]);

  const openEditEditor = useCallback((prompt: Prompt) => {
    setEditingPrompt(prompt);
    setEditorTitle(prompt.title);
    setEditorDescription(prompt.description);
    setEditorText(prompt.text);
    setEditorCategory(getCategoryId(prompt));
    setEditorOpen(true);
  }, []);

  const closeEditor = useCallback(() => {
    setEditorOpen(false);
    setEditingPrompt(null);
  }, []);

  // --- Save prompt ---
  const handleSavePrompt = useCallback(async () => {
    if (!editorTitle.trim() || !editorText.trim() || !editorCategory) {
      addToast('Titel, Text und Kategorie sind Pflichtfelder.', 'error');
      return;
    }
    setEditorSaving(true);
    try {
      if (editingPrompt) {
        await api.updatePrompt(editingPrompt._id, {
          title: editorTitle,
          description: editorDescription,
          text: editorText,
          category: editorCategory,
        });
        addToast('Prompt aktualisiert');
      } else {
        await api.createPrompt({
          title: editorTitle,
          description: editorDescription,
          text: editorText,
          category: editorCategory,
        });
        addToast('Prompt erstellt');
      }
      closeEditor();
      await Promise.all([fetchPrompts(), fetchCategories()]);
    } catch (err: any) {
      addToast(err?.response?.data?.error || 'Speichern fehlgeschlagen', 'error');
    } finally {
      setEditorSaving(false);
    }
  }, [editingPrompt, editorTitle, editorDescription, editorText, editorCategory, addToast, closeEditor, fetchPrompts, fetchCategories]);

  // --- Delete prompt ---
  const handleDeletePrompt = useCallback(async (id: string) => {
    try {
      await api.deletePrompt(id);
      addToast('Prompt gelöscht');
      setDeleteConfirmId(null);
      await Promise.all([fetchPrompts(), fetchCategories()]);
    } catch {
      addToast('Löschen fehlgeschlagen', 'error');
    }
  }, [addToast, fetchPrompts, fetchCategories]);

  // --- Add category ---
  const handleAddCategory = useCallback(async () => {
    if (!newCategoryName.trim()) return;
    try {
      await api.createCategory(newCategoryName.trim());
      setNewCategoryName('');
      setAddingCategory(false);
      addToast('Kategorie erstellt');
      await fetchCategories();
    } catch (err: any) {
      addToast(err?.response?.data?.error || 'Kategorie erstellen fehlgeschlagen', 'error');
    }
  }, [newCategoryName, addToast, fetchCategories]);

  // --- Add subcategory ---
  const handleAddSubcategory = useCallback(async (parentId: string) => {
    if (!newSubcategoryName.trim()) return;
    try {
      await api.createCategory(newSubcategoryName.trim(), parentId);
      setNewSubcategoryName('');
      setAddingSubcategoryFor(null);
      setExpandedParents(prev => new Set(prev).add(parentId));
      addToast('Unterkategorie erstellt');
      await fetchCategories();
    } catch (err: any) {
      addToast(err?.response?.data?.error || 'Unterkategorie erstellen fehlgeschlagen', 'error');
    }
  }, [newSubcategoryName, addToast, fetchCategories]);

  // --- Delete category ---
  const handleDeleteCategory = useCallback(async (id: string) => {
    try {
      await api.deleteCategory(id);
      if (activeCategory === id) setActiveCategory(null);
      addToast('Kategorie gelöscht');
      await fetchCategories();
    } catch (err: any) {
      addToast(err?.response?.data?.error || 'Löschen fehlgeschlagen', 'error');
    }
  }, [activeCategory, addToast, fetchCategories]);

  // --- Keyboard handler for editor ---
  const handleEditorKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') closeEditor();
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') handleSavePrompt();
  }, [closeEditor, handleSavePrompt]);

  // --- Sort labels ---
  const sortLabels: Record<SortMode, string> = {
    newest: 'Neueste',
    oldest: 'Älteste',
    title: 'A–Z',
  };

  // --- Render ---

  if (loading) {
    return (
      <div className="pc-loading">
        <Loader2 className="pc-spinner" size={24} />
        <span>Laden...</span>
      </div>
    );
  }

  return (
    <div className="pc-root" onKeyDown={handleEditorKeyDown}>
      {/* Mobile Sidebar Toggle */}
      <button
        className="pc-mobile-toggle"
        onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
        aria-label="Kategorien öffnen"
      >
        <Menu size={20} />
        <span>Kategorien</span>
      </button>

      {/* Sidebar */}
      <aside className={`pc-sidebar ${mobileSidebarOpen ? 'pc-sidebar--open' : ''}`}>
        <div className="pc-sidebar-header">
          <h3 className="pc-sidebar-title">Kategorien</h3>
          <button
            className="pc-sidebar-close"
            onClick={() => setMobileSidebarOpen(false)}
            aria-label="Schließen"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="pc-sidebar-nav" role="navigation" aria-label="Kategorien">
          {/* All prompts */}
          <button
            className={`pc-sidebar-item ${activeCategory === null ? 'pc-sidebar-item--active' : ''}`}
            onClick={() => { setActiveCategory(null); setMobileSidebarOpen(false); }}
          >
            <LayoutList size={16} />
            <span className="pc-sidebar-item-label">Alle Prompts</span>
            <span className="pc-sidebar-item-count">{totalCount}</span>
          </button>

          {/* Category tree */}
          {parentCategories.map(cat => {
            const children = getChildren(cat._id);
            const hasChildren = children.length > 0;
            const isExpanded = expandedParents.has(cat._id);
            const parentCount = getParentCount(cat._id);
            const isEmpty = parentCount === 0 && !hasChildren;

            return (
              <div key={cat._id} className="pc-sidebar-group">
                <div className="pc-sidebar-item-wrapper">
                  <button
                    className={`pc-sidebar-item ${activeCategory === cat._id ? 'pc-sidebar-item--active' : ''}`}
                    onClick={() => { setActiveCategory(cat._id); setMobileSidebarOpen(false); }}
                  >
                    {hasChildren ? (
                      <button
                        className="pc-sidebar-expand"
                        onClick={(e) => { e.stopPropagation(); toggleParentExpand(cat._id); }}
                        aria-label={isExpanded ? 'Einklappen' : 'Ausklappen'}
                      >
                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </button>
                    ) : (
                      <FolderOpen size={16} />
                    )}
                    <span className="pc-sidebar-item-label">{cat.name}</span>
                    <span className="pc-sidebar-item-count">{parentCount}</span>
                  </button>
                  <div className="pc-sidebar-item-actions">
                    <button
                      className="pc-sidebar-item-add-sub"
                      onClick={(e) => { e.stopPropagation(); setAddingSubcategoryFor(cat._id); setNewSubcategoryName(''); setExpandedParents(prev => new Set(prev).add(cat._id)); }}
                      title="Unterkategorie hinzufügen"
                      aria-label={`Unterkategorie zu ${cat.name}`}
                    >
                      <Plus size={11} />
                    </button>
                    {isEmpty && (
                      <button
                        className="pc-sidebar-item-delete"
                        onClick={(e) => { e.stopPropagation(); handleDeleteCategory(cat._id); }}
                        title="Leere Kategorie löschen"
                        aria-label={`${cat.name} löschen`}
                      >
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>

                {/* Subcategories */}
                {hasChildren && isExpanded && (
                  <div className="pc-sidebar-children">
                    {children.map(sub => (
                      <div key={sub._id} className="pc-sidebar-item-wrapper">
                        <button
                          className={`pc-sidebar-item pc-sidebar-item--child ${activeCategory === sub._id ? 'pc-sidebar-item--active' : ''}`}
                          onClick={() => { setActiveCategory(sub._id); setMobileSidebarOpen(false); }}
                        >
                          <Folder size={14} />
                          <span className="pc-sidebar-item-label">{sub.name}</span>
                          <span className="pc-sidebar-item-count">{sub.count}</span>
                        </button>
                        {sub.count === 0 && (
                          <button
                            className="pc-sidebar-item-delete"
                            onClick={(e) => { e.stopPropagation(); handleDeleteCategory(sub._id); }}
                            title="Leere Unterkategorie löschen"
                            aria-label={`${sub.name} löschen`}
                          >
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Add subcategory inline input */}
                {addingSubcategoryFor === cat._id && (
                  <div className="pc-sidebar-children">
                    <div className="pc-add-subcategory">
                      <input
                        className="pc-add-category-input"
                        value={newSubcategoryName}
                        onChange={e => setNewSubcategoryName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleAddSubcategory(cat._id); if (e.key === 'Escape') setAddingSubcategoryFor(null); }}
                        placeholder="Unterkategorie..."
                        autoFocus
                      />
                      <button className="pc-add-category-btn" onClick={() => handleAddSubcategory(cat._id)} aria-label="Unterkategorie hinzufügen">
                        <Check size={14} />
                      </button>
                      <button className="pc-add-category-btn" onClick={() => setAddingSubcategoryFor(null)} aria-label="Abbrechen">
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Add category */}
        <div className="pc-sidebar-footer">
          {addingCategory ? (
            <div className="pc-add-category">
              <input
                className="pc-add-category-input"
                value={newCategoryName}
                onChange={e => setNewCategoryName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddCategory(); if (e.key === 'Escape') setAddingCategory(false); }}
                placeholder="Kategoriename..."
                autoFocus
              />
              <button className="pc-add-category-btn" onClick={handleAddCategory} aria-label="Kategorie hinzufügen">
                <Check size={14} />
              </button>
              <button className="pc-add-category-btn" onClick={() => setAddingCategory(false)} aria-label="Abbrechen">
                <X size={14} />
              </button>
            </div>
          ) : (
            <button className="pc-sidebar-add" onClick={() => setAddingCategory(true)}>
              <Plus size={14} />
              <span>Kategorie</span>
            </button>
          )}
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div className="pc-sidebar-overlay" onClick={() => setMobileSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="pc-main">
        {/* Top Bar */}
        <div className="pc-topbar">
          <h2 className="pc-page-title">Promptsammlung</h2>

          <div className="pc-topbar-actions">
            {/* Search */}
            <div className="pc-search">
              <Search size={16} className="pc-search-icon" />
              <input
                className="pc-search-input"
                type="text"
                placeholder="Prompts durchsuchen..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                aria-label="Prompts suchen"
              />
              {searchQuery && (
                <button className="pc-search-clear" onClick={() => setSearchQuery('')} aria-label="Suche leeren">
                  <X size={14} />
                </button>
              )}
            </div>

            {/* Sort */}
            <div className="pc-sort" ref={sortDropdownRef}>
              <button
                className="pc-sort-btn"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                aria-label="Sortierung"
              >
                <SortAsc size={16} />
                <span>{sortLabels[sortMode]}</span>
              </button>
              {showSortDropdown && (
                <div className="pc-sort-dropdown">
                  {(Object.keys(sortLabels) as SortMode[]).map(mode => (
                    <button
                      key={mode}
                      className={`pc-sort-option ${sortMode === mode ? 'pc-sort-option--active' : ''}`}
                      onClick={() => { setSortMode(mode); setShowSortDropdown(false); }}
                    >
                      {sortLabels[mode]}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Create */}
            <button className="pc-create-btn" onClick={openCreateEditor}>
              <Plus size={16} />
              <span>Prompt</span>
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="pc-error">
            <AlertCircle size={16} />
            <span>{error}</span>
            <button onClick={() => setError(null)}><X size={14} /></button>
          </div>
        )}

        {/* Active category label */}
        {activeCategory && (
          <div className="pc-active-filter">
            <span>Kategorie: <strong>{categories.find(c => c._id === activeCategory)?.name}</strong></span>
            <button onClick={() => setActiveCategory(null)} aria-label="Filter entfernen">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Prompt list */}
        <div className="pc-prompts">
          {prompts.length === 0 ? (
            <div className="pc-empty">
              <FileText size={40} strokeWidth={1} />
              {debouncedSearch ? (
                <>
                  <p className="pc-empty-title">Keine Treffer</p>
                  <p className="pc-empty-sub">Für &quot;{debouncedSearch}&quot; wurden keine Prompts gefunden.</p>
                  <button className="pc-empty-action" onClick={() => setSearchQuery('')}>
                    Suche zurücksetzen
                  </button>
                </>
              ) : (
                <>
                  <p className="pc-empty-title">Noch keine Prompts</p>
                  <p className="pc-empty-sub">Erstelle deinen ersten Prompt, um loszulegen.</p>
                  <button className="pc-empty-action" onClick={openCreateEditor}>
                    <Plus size={14} />
                    Prompt hinzufügen
                  </button>
                </>
              )}
            </div>
          ) : (
            <AnimatePresence mode="popLayout">
              {prompts.map(prompt => {
                const isExpanded = expandedCards.has(prompt._id);
                const isLong = prompt.text.split('\n').length > 6 || prompt.text.length > 400;

                return (
                  <motion.div
                    key={prompt._id}
                    layout
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.15 }}
                    className="pc-card"
                  >
                    {/* Card Header */}
                    <div className="pc-card-header">
                      <div className="pc-card-meta">
                        <h3 className="pc-card-title">{prompt.title}</h3>
                        {prompt.description && (
                          <p className="pc-card-desc">{prompt.description}</p>
                        )}
                        <div className="pc-card-info">
                          <span className="pc-card-category">{getCategoryName(prompt)}</span>
                          <span className="pc-card-time">{timeAgo(prompt.updatedAt)}</span>
                        </div>
                      </div>

                      <div className="pc-card-actions">
                        <button
                          className={`pc-action-btn pc-action-copy ${copiedId === prompt._id ? 'pc-action-btn--success' : ''}`}
                          onClick={() => handleCopy(prompt)}
                          title="Kopieren"
                          aria-label="Prompt kopieren"
                        >
                          {copiedId === prompt._id ? <Check size={15} /> : <Copy size={15} />}
                        </button>
                        <button
                          className="pc-action-btn"
                          onClick={() => openEditEditor(prompt)}
                          title="Bearbeiten"
                          aria-label="Prompt bearbeiten"
                        >
                          <Pencil size={15} />
                        </button>
                        {deleteConfirmId === prompt._id ? (
                          <div className="pc-delete-confirm">
                            <button
                              className="pc-action-btn pc-action-btn--danger"
                              onClick={() => handleDeletePrompt(prompt._id)}
                              title="Löschen bestätigen"
                            >
                              <Check size={15} />
                            </button>
                            <button
                              className="pc-action-btn"
                              onClick={() => setDeleteConfirmId(null)}
                              title="Abbrechen"
                            >
                              <X size={15} />
                            </button>
                          </div>
                        ) : (
                          <button
                            className="pc-action-btn pc-action-btn--subtle"
                            onClick={() => setDeleteConfirmId(prompt._id)}
                            title="Löschen"
                            aria-label="Prompt löschen"
                          >
                            <Trash2 size={15} />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Card Body - Prompt Text */}
                    <div className={`pc-card-body ${!isExpanded && isLong ? 'pc-card-body--clamped' : ''}`}>
                      <pre className="pc-card-text">{prompt.text}</pre>
                    </div>

                    {isLong && (
                      <button className="pc-card-expand" onClick={() => toggleExpand(prompt._id)}>
                        {isExpanded ? (
                          <><ChevronUp size={14} /> Weniger anzeigen</>
                        ) : (
                          <><ChevronDown size={14} /> Mehr anzeigen</>
                        )}
                      </button>
                    )}
                  </motion.div>
                );
              })}
            </AnimatePresence>
          )}
        </div>
      </div>

      {/* Editor Side Panel */}
      <AnimatePresence>
        {editorOpen && (
          <>
            <motion.div
              className="pc-editor-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeEditor}
            />
            <motion.aside
              className="pc-editor"
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 30, stiffness: 300 }}
              onKeyDown={handleEditorKeyDown}
            >
              <div className="pc-editor-header">
                <h3>{editingPrompt ? 'Prompt bearbeiten' : 'Neuer Prompt'}</h3>
                <button className="pc-editor-close" onClick={closeEditor} aria-label="Schließen">
                  <X size={18} />
                </button>
              </div>

              <div className="pc-editor-body">
                <label className="pc-editor-label">
                  Titel *
                  <input
                    className="pc-editor-input"
                    value={editorTitle}
                    onChange={e => setEditorTitle(e.target.value)}
                    placeholder="z. B. Code-Review Prompt"
                    autoFocus
                  />
                </label>

                <label className="pc-editor-label">
                  Kategorie *
                  <select
                    className="pc-editor-select"
                    value={editorCategory}
                    onChange={e => setEditorCategory(e.target.value)}
                  >
                    <option value="">Kategorie wählen...</option>
                    {parentCategories.map(cat => {
                      const children = getChildren(cat._id);
                      return (
                        <React.Fragment key={cat._id}>
                          <option value={cat._id}>{cat.name}</option>
                          {children.map(sub => (
                            <option key={sub._id} value={sub._id}>&nbsp;&nbsp;↳ {sub.name}</option>
                          ))}
                        </React.Fragment>
                      );
                    })}
                  </select>
                </label>

                <label className="pc-editor-label">
                  Beschreibung
                  <input
                    className="pc-editor-input"
                    value={editorDescription}
                    onChange={e => setEditorDescription(e.target.value)}
                    placeholder="Kurze Beschreibung (optional)"
                  />
                </label>

                <label className="pc-editor-label pc-editor-label--grow">
                  Prompt-Text *
                  <textarea
                    className="pc-editor-textarea"
                    value={editorText}
                    onChange={e => setEditorText(e.target.value)}
                    placeholder="Dein Prompt-Text..."
                  />
                </label>
              </div>

              <div className="pc-editor-footer">
                <button className="pc-btn pc-btn--ghost" onClick={closeEditor}>
                  Abbrechen
                </button>
                <button
                  className="pc-btn pc-btn--primary"
                  onClick={handleSavePrompt}
                  disabled={editorSaving || !editorTitle.trim() || !editorText.trim() || !editorCategory}
                >
                  {editorSaving ? <Loader2 size={14} className="pc-spinner" /> : null}
                  {editingPrompt ? 'Speichern' : 'Erstellen'}
                </button>
              </div>

              <div className="pc-editor-hint">
                <kbd>Esc</kbd> schließen &middot; <kbd>Ctrl+Enter</kbd> speichern
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Toasts */}
      <Toast toasts={toasts} onRemove={removeToast} />
    </div>
  );
};

export default PromptCollection;
